<template>
  <AdminLayout @logout="logout">
    <!-- Top Loading Bar -->
    <div 
      v-if="isProcessing" 
      class="top-loading-bar"
      :style="{ width: loadingProgress + '%' }"
    ></div>
    
    <div class="dynamic-page-container">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center min-h-64">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"
        ></div>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="bg-red-50 border border-red-200 rounded-lg p-6 mx-4"
      >
        <div class="flex items-center">
          <svg
            class="w-6 h-6 text-red-600 mr-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.98-.833-2.75 0L3.064 16.5c-.77.833.192 2.5 1.732 2.5z"
            ></path>
          </svg>
          <div>
            <h3 class="text-red-800 font-medium">Error Loading Page</h3>
            <p class="text-red-700 text-sm mt-1">{{ error }}</p>
          </div>
        </div>
        <button
          @click="fetchContent"
          class="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>

      <!-- Content -->
      <div
        v-else-if="processedHtml"
        v-html="processedHtml"
        class="dynamic-content"
        @click="handleContentClick"
      ></div>

      <!-- No Content -->
      <div v-else class="text-center py-12 text-gray-500">
        <p>No content available for this page.</p>
      </div>
    </div>

    <!-- Sidebar Overlay -->
    <div
      v-if="sidebarOpen"
      class="fixed inset-0 z-50 overflow-hidden"
      @click="closeSidebar"
    >
      <!-- Background Overlay -->
      <div
        class="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
      ></div>

      <!-- Sidebar -->
      <div
        class="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl transform transition-transform sm:max-w-2xl md:max-w-3xl"
        :class="{
          'translate-x-0': sidebarOpen,
          'translate-x-full': !sidebarOpen,
        }"
        @click.stop
      >
        <!-- Sidebar Header -->
        <div
          class="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50"
        >
          <div class="flex-1 min-w-0">
            <h2 class="text-lg font-semibold text-gray-900 truncate">
              {{ sidebarTitle }}
            </h2>
            <p class="text-sm text-gray-500 truncate mt-1">
              {{ sidebarUrl }}
            </p>
          </div>
          <button
            @click="closeSidebar"
            class="ml-4 flex-shrink-0 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>

        <!-- Sidebar Content -->
        <div class="flex-1 overflow-hidden" style="height: calc(100vh - 150px)">
          <!-- Loading State -->
          <div
            v-if="sidebarLoading"
            class="flex justify-center items-center h-64"
          >
            <div
              class="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"
            ></div>
          </div>

          <!-- Error State -->
          <div v-else-if="sidebarError" class="p-4">
            <div class="bg-red-50 border border-red-200 rounded-lg p-4">
              <div class="flex items-center">
                <svg
                  class="w-5 h-5 text-red-600 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.98-.833-2.75 0L3.064 16.5c-.77.833.192 2.5 1.732 2.5z"
                  ></path>
                </svg>
                <div>
                  <h3 class="text-red-800 font-medium">
                    Error Loading Content
                  </h3>
                  <p class="text-red-700 text-sm mt-1">{{ sidebarError }}</p>
                </div>
              </div>
              <div class="mt-4 flex space-x-3">
                <button
                  @click="loadSidebarContent"
                  class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
                >
                  Retry
                </button>
                <a
                  :href="sidebarUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm"
                >
                  Open in New Tab
                </a>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div v-else class="h-full">
            <iframe
              v-if="sidebarContent && isValidUrl(sidebarUrl)"
              :src="sidebarUrl"
              class="w-full h-full border-0 sidebar-iframe"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
              referrerpolicy="no-referrer-when-downgrade"
              loading="lazy"
            ></iframe>
            <div v-else-if="sidebarContent" class="p-4 overflow-auto h-full">
              <div v-html="sidebarContent" class="prose max-w-none"></div>
            </div>
            <!-- Fallback message for blocked content -->
            <div
              v-else-if="sidebarError && sidebarError.includes('blocked')"
              class="p-4 text-center"
            >
              <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <div class="flex items-center justify-center mb-4">
                  <svg
                    class="w-12 h-12 text-yellow-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.98-.833-2.75 0L3.064 16.5c-.77.833.192 2.5 1.732 2.5z"
                    ></path>
                  </svg>
                </div>
                <h3 class="text-lg font-semibold text-yellow-800 mb-2">
                  Content Blocked
                </h3>
                <p class="text-yellow-700 mb-4">
                  This content has been blocked by your browser's security
                  settings (likely Brave's ad blocker or privacy shields).
                </p>
                <div class="space-y-2">
                  <p class="text-sm text-yellow-600">
                    <strong>To view this content:</strong>
                  </p>
                  <ul class="text-sm text-yellow-600 text-left space-y-1">
                    <li>• Disable Brave Shields for this site</li>
                    <li>• Allow all scripts and frames</li>
                    <li>• Or click "Open in New Tab" below</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sidebar Footer -->
        <div class="border-t border-gray-200 bg-gray-50 px-4 py-3">
          <div class="flex items-center justify-between">
            <div class="flex space-x-3">
              <a
                :href="sidebarUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <svg
                  class="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  ></path>
                </svg>
                Open in New Tab
              </a>
              <button
                @click="copySidebarUrl"
                class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <svg
                  class="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                  ></path>
                </svg>
                Copy URL
              </button>
            </div>
            <button
              @click="closeSidebar"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Team Member Popup Modal -->
    <section
      v-if="showTeamPopup"
      id="team-popup"
      class="team-popup-overlay"
      @click.self="closeTeamPopup"
    >
      <div class="team-popup-container">
        <div class="team-popup-content">
          <button type="button" class="team-popup-close" @click="closeTeamPopup">✕</button>
          <div class="grid gap-2 md:grid-cols-30-70 member-lightbox">
            <div id="popup_image_container" class="relative profile-lt-box h-55 md:h-30">
              <img :src="teamPopupData.image || 'https://uptodatewebdesign.s3.eu-west-3.amazonaws.com/uploads/placeholder-1741669842731.png'" class="popup-team-img" />
            </div>
            <div class="flex flex-column f-justify-center">
              <div class="mb-4">
                <h3 id="popup_name" class="text-bold mb-3">{{ teamPopupData.name }}</h3>
                <p id="popup_position" class="text-bold mb-0">{{ teamPopupData.position }}</p>
              </div>
              <div id="popup_desc" class="mb-3 popup_desc" v-html="teamPopupData.description"></div>

              <!-- Email and Contact Info -->
              <div v-if="teamPopupData.email || teamPopupData.contact" class="team-contact-info">
                <p v-if="teamPopupData.email" class="mb-2 flex items-center">
                  <span class="contact-icon">📧</span>
                  <a :href="'mailto:' + teamPopupData.email" class="text-theme hover:underline">{{ teamPopupData.email }}</a>
                </p>
                <p v-if="teamPopupData.contact" class="mb-0 flex items-center">
                  <span class="contact-icon">📞</span>
                  <a :href="'tel:' + teamPopupData.contact.replace(/\s/g, '')" class="text-theme hover:underline">{{ teamPopupData.contact }}</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </AdminLayout>
</template>

<script>
import AdminLayout from "@/components/AdminLayout.vue";

export default {
  name: "DynamicPage",
  components: {
    AdminLayout,
  },
  props: {
    siteId: {
      type: String,
      required: true,
    },
    pageId: {
      type: String,
      required: true,
    },
    showInSidebar: {
      type: Boolean,
      default: false,
    },
    enableUTDInit: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      htmlContent: "",
      processedHtml: "",
      // Sidebar state
      sidebarOpen: false,
      sidebarLoading: false,
      sidebarError: null,
      sidebarContent: "",
      sidebarUrl: "",
      sidebarTitle: "",
      globalCode: [],
      globalCodeLoaded: false,
      isFetching: false,
      isInitializing: false,
      hasInitialized: false,
      hiddenElements: null,
      // Loading bar state
      isProcessing: false,
      loadingProgress: 0,
      // Team popup state
      showTeamPopup: false,
      teamPopupData: {
        name: '',
        position: '',
        description: '',
        image: '',
        email: '',
        contact: ''
      },
      teamMembersData: [] // Store all team data from API
    };
  },
  watch: {
    pageId: {
      immediate: true,
      async handler(newPageId, oldPageId) {
        console.log('Watcher fired:', { newPageId, oldPageId });
        
        // Skip if pageId hasn't actually changed (prevents duplicate on initial load)
        if (newPageId === oldPageId && oldPageId !== undefined) {
          console.log('Same pageId, skipping');
          return;
        }
        
        // Reset initialization flag when changing pages
        this.hasInitialized = false;
        this.isInitializing = false;
        
        // Ensure global code is loaded before fetching content
        if (!this.globalCodeLoaded) {
          console.log('Loading global code...');
          await this.getGlobalCode();
        }
        
        console.log('Calling fetchContent from watcher');
        await this.fetchContent();
      },
    },
  },
  created(){
    // Set flags IMMEDIATELY to prevent auto-initialization
    if (typeof window !== "undefined" && this.enableUTDInit) {
      window.UTD_MANUAL_INIT = true;
      window.UTD_DISABLE_AUTO_INIT = true;
      
      // Also override the renderPostElement function to prevent auto-execution
      if (window.UTDfnc && window.UTDfnc.initialize && !window.UTDfnc._originalRenderPostElement) {
        console.log("🛡️ Wrapping UTDfnc.initialize.renderPostElement to prevent auto-init");
        window.UTDfnc._originalRenderPostElement = window.UTDfnc.initialize.renderPostElement;
        window.UTDfnc._manualInitOnly = true;
        
        // Replace with a controlled version
        window.UTDfnc.initialize.renderPostElement = (...args) => {
          if (window.UTDfnc._manualInitOnly && !window.UTDfnc._allowRender) {
            console.log("🚫 Blocked auto renderPostElement call");
            return;
          }
          console.log("✅ Allowing manual renderPostElement call");
          return window.UTDfnc._originalRenderPostElement.apply(window.UTDfnc.initialize, args);
        };
      }
      
      console.log("Early: Set UTD flags to prevent auto-initialization");
    }
    // Global code will be fetched in the watcher
  },
  mounted() {
    // Add keyboard event listener for ESC key
    document.addEventListener("keydown", this.handleKeydown);

    // Listen for teams-loaded event from UTD script
    window.addEventListener('teams-loaded', this.handleTeamsLoaded);
  },
  beforeDestroy() {
    // Clean up scripts when component is destroyed
    this.cleanupScripts();
    // Remove keyboard event listener
    document.removeEventListener("keydown", this.handleKeydown);
    // Remove teams-loaded event listener
    window.removeEventListener('teams-loaded', this.handleTeamsLoaded);
  },
  methods: {
    startLoadingBar() {
      this.isProcessing = true;
      this.loadingProgress = 0;
      
      // Simulate progress
      const interval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += Math.random() * 10;
        }
        
        if (!this.isProcessing) {
          clearInterval(interval);
        }
      }, 200);
    },
    completeLoadingBar() {
      this.loadingProgress = 100;
      setTimeout(() => {
        this.isProcessing = false;
        this.loadingProgress = 0;
      }, 300);
    },
    hideUTDElements() {
      console.log("Hiding UTD elements from rendered HTML...");
      
      // Find and temporarily hide all elements that trigger auto-init
      // Based on the script code, it looks for: .utdbuilder-label.utd-post, .utdbuilder-label.utd-page, .utdbuilder-label.utd-links
      const selectors = [
        '.utdbuilder-label.utd-post',
        '.utdbuilder-label.utd-page',
        '.utdbuilder-label.utd-links',
        '.utdbuilder-label',
        '[data-utd-posts]',
        '[data-blog-posts]',
        '[data-config]'
      ];
      
      this.hiddenElements = [];
      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        console.log(`Found ${elements.length} elements matching "${selector}"`);
        elements.forEach(el => {
          // Check if already hidden to avoid duplicates
          if (!this.hiddenElements.find(item => item.element === el)) {
            const currentDisplay = el.style.display || '';
            el.style.display = 'none';
            // Also add a class to mark it as hidden by us
            el.classList.add('utd-temp-hidden');
            this.hiddenElements.push({ element: el, display: currentDisplay });
          }
        });
      });
      
      console.log(`✅ Temporarily hidden ${this.hiddenElements.length} elements from DOM to prevent auto-init`);
      
      if (this.hiddenElements.length === 0) {
        console.warn('⚠️ WARNING: No UTD elements found to hide! The script may auto-initialize.');
      }
    },
    async getGlobalCode(){
      try {
        // Check if cached data exists and is still valid (24 hours)
        const cacheKey = `globalCode_${this.siteId}`;
        const cacheTimestampKey = `globalCode_${this.siteId}_timestamp`;
        const cachedData = localStorage.getItem(cacheKey);
        const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
        
        // Check if cache is valid (less than 24 hours old)
        if (cachedData && cacheTimestamp) {
          const now = Date.now();
          const cacheAge = now - parseInt(cacheTimestamp);
          const oneDayInMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
          
          if (cacheAge < oneDayInMs) {
            // Use cached data
            console.log('Using cached global code');
            this.globalCode = JSON.parse(cachedData);
            this.globalCodeLoaded = true;
            return;
          }
        }
        
        // Fetch fresh data if cache is invalid or doesn't exist
        console.log('Fetching fresh global code');
        const response = await fetch(`https://dev.uptodateconnect.com/api/v1/site-builder/resources?siteId=${this.siteId}&resources=globalCode`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data && data.globalCode && Array.isArray(data.globalCode)) {
          this.globalCode = data.globalCode;
          this.globalCodeLoaded = true;
          
          // Cache the data
          localStorage.setItem(cacheKey, JSON.stringify(data.globalCode));
          localStorage.setItem(cacheTimestampKey, Date.now().toString());
        }
      } catch (error) {
        console.error("Error fetching global code:", error);
        // Mark as loaded even if it fails to prevent blocking
        this.globalCodeLoaded = true;
      }
    },
    async fetchContent() {
      // Prevent duplicate fetches
      if (this.isFetching) {
        console.log('Already fetching, skipping duplicate call');
        return;
      }
      
      this.startLoadingBar(); // Start top loading bar
      this.isFetching = true;
      this.loading = true;
      this.error = null;
      this.htmlContent = "";

      try {
        if (!this.pageId) {
          throw new Error("Page ID is required");
        }

        const response = await fetch(
          `https://www.uptodateconnect.com/api/v1/site-builder/sites-page-html-export?siteId=${this.siteId}&pageId=${this.pageId}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        this.htmlContent = await response.text();
        this.processContent();
        
        // Wait for DOM to update with the new HTML, then hide elements before loading scripts
        // Use setTimeout in addition to $nextTick to ensure v-html has fully rendered
        this.$nextTick(() => {
          setTimeout(() => {
            // Hide elements that trigger auto-init BEFORE loading scripts
            if (this.enableUTDInit) {
              //this.hideUTDElements();
            }
            
            // Then handle script loading after a brief delay
            setTimeout(() => {
              this.handleScriptLoading();
            }, 50);
          }, 100);
        });
      } catch (err) {
        console.error("Error fetching page content:", err);
        this.error = err.message || "Failed to load page content";
      } finally {
        this.loading = false;
        this.isFetching = false;
      }
    },
    processContent() {
      if (!this.htmlContent) {
        this.processedHtml = "";
        return;
      }

      // Remove DOCTYPE, html, head, and body tags to extract only the content
      let processedHtml = this.htmlContent;

      // Remove DOCTYPE declaration
      processedHtml = processedHtml.replace(/<!DOCTYPE[^>]*>/gi, "");

      // Extract content from body tag if present
      const bodyMatch = processedHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/gi);
      if (bodyMatch) {
        processedHtml = bodyMatch[0];
        // Remove the body tags themselves
        processedHtml = processedHtml.replace(/<\/?body[^>]*>/gi, "");
      }

      // Remove html tags
      processedHtml = processedHtml.replace(/<\/?html[^>]*>/gi, "");

      // Remove head section completely
      processedHtml = processedHtml.replace(/<head[\s\S]*?<\/head>/gi, "");

      // Remove script tags that might interfere with the layout
      processedHtml = processedHtml.replace(/<script[\s\S]*?<\/script>/gi, "");

      // Remove style tags that might override our layout
      processedHtml = processedHtml.replace(/<style[\s\S]*?<\/style>/gi, "");

      // Clean up extra whitespace
      processedHtml = processedHtml.trim();

      // Separate global code by section (header vs footer)
      let headerCode = '';
      let footerCode = '';
      
      if (Array.isArray(this.globalCode)) {
        this.globalCode.forEach(item => {
          if (item.code) {
            if (item.section === 'header') {
              headerCode += item.code;
            } else if (item.section === 'footer') {
              // Only include footer code if UTD init is disabled
              // Otherwise we'll load scripts manually to prevent double initialization
              if (!this.enableUTDInit) {
                footerCode += item.code;
              }
            }
          }
        });
      }

      // Add the preview styling like in index.vue with global code
      this.processedHtml = `
        ${headerCode}
        <style>
          :root {
            --theme: #e31c18;
            --block-padding: 6rem;
            --theme-hover: #354046;
          }
          body {
            margin: 0;
          }
        </style>
        ${processedHtml}
        ${footerCode}
      `;
    },
    handleScriptLoading() {
      // Skip if UTD initialization is disabled for this page
      if (!this.enableUTDInit) {
        console.log("UTD initialization disabled for this page via prop");
        return;
      }
      
      // Check if we're coming from navigation (not a direct page refresh)
      const isFromNavigation =
        this.$route.params.fromNavigation ||
        sessionStorage.getItem("isNavigating") === "true";

      // Check if scripts are already loaded
      const scriptsLoaded = this.areScriptsLoaded();

      console.log("Script loading check:", {
        isFromNavigation,
        scriptsLoaded,
        UTDfnc: typeof window.UTDfnc,
        routePath: this.$route.path,
        enableUTDInit: this.enableUTDInit,
        hasInitialized: this.hasInitialized,
        isInitializing: this.isInitializing
      });

      if (scriptsLoaded && window.UTDfnc) {
        // Scripts are loaded, check if already initialized to prevent double-init on refresh
        if (this.hasInitialized) {
          console.log("⚠️ Scripts already loaded AND already initialized, skipping to prevent duplicate");
          return;
        }
        
        // Scripts are loaded, restore hidden elements if any, then run initialization
        console.log("Scripts already loaded, running initialization only");
        
        // Restore hidden elements first (if navigation happened after hiding)
        if (this.hiddenElements) {
          console.log(`Restoring ${this.hiddenElements.length} hidden elements before init...`);
          this.hiddenElements.forEach(({ element, display }) => {
            element.style.display = display;
            element.classList.remove('utd-temp-hidden');
          });
          this.hiddenElements = null;
        }
        
        // Then run initialization
        this.runUTDInitialization();
      } else if (!scriptsLoaded) {
        // Scripts not loaded, load them first
        console.log("Scripts not loaded, loading scripts first");
        this.loadDynamicScripts();
      } else {
        // Scripts loaded but UTDfnc not available, wait and try again
        console.log("Scripts loaded but UTDfnc not ready, waiting...");
        setTimeout(() => {
          if (window.UTDfnc) {
            this.runUTDInitialization();
          } else {
            this.loadDynamicScripts();
          }
        }, 500);
      }

      // Clear navigation flag
      sessionStorage.removeItem("isNavigating");
    },
    areScriptsLoaded() {
      const requiredScripts = [
        "https://unpkg.com/swiper@6.1.2/swiper-bundle.min.js",
        "https://cdnjs.cloudflare.com/ajax/libs/jquery/3.4.1/jquery.min.js",
        "https://cdnjs.cloudflare.com/ajax/libs/jquery.isotope/3.0.6/isotope.pkgd.min.js",
        "https://uptodatewebdesign.s3.eu-west-3.amazonaws.com/cdn/dist/v2/app.min.multi-platform.js",
      ];

      return requiredScripts.every((src) => {
        return document.querySelector(`script[src="${src}"]`) !== null;
      });
    },
    runUTDInitialization() {
      // Prevent duplicate initialization - check both flags
      if (this.isInitializing || this.hasInitialized) {
        console.log("UTD already initialized or initializing, skipping duplicate call", {
          isInitializing: this.isInitializing,
          hasInitialized: this.hasInitialized
        });
        return;
      }
      
      this.isInitializing = true;
      console.log("Running UTD initialization...");

      // Set global variables and flags to prevent auto-init
      if (typeof window !== "undefined") {
        window.utdElementSiteUrl = this.siteId;
        window.BLOGGER_URL = "https://selectairintranet.wpcomstaging.com/";
        window.BLOG_PLATFORM = "wordpress";
        window.WORDPRESS_URL = "https://selectairintranet.wpcomstaging.com/";
        
        // Ensure auto-init is disabled
        window.UTD_MANUAL_INIT = true;
        window.UTD_DISABLE_AUTO_INIT = true;
        
        console.log("Global variables set and auto-init disabled");
      }

      // Run the UTD initialization - only once per page load
      let attemptCount = 0;
      const maxAttempts = 100; // 10 seconds max
      
      let interval = setInterval(() => {
        attemptCount++;
        
        if (window.UTDfnc) {
          clearInterval(interval);
          try {
            console.log("UTDfnc available, calling init functions...");
            
            // Check if content has already been rendered by the script
            // const hasRenderedContent = document.querySelector('.utd-initialized, .post__item, .swiper-initialized');
            
            // if (hasRenderedContent) {
            //   console.log("UTD content already rendered by script, skipping manual init");
            //   this.hasInitialized = true;
            //   this.isInitializing = false;
            //   return;
            // }
            
            // Temporarily allow rendering for our manual call
            if (window.UTDfnc._manualInitOnly) {
              window.UTDfnc._allowRender = true;
            }
            
            // Only call renderPostElement, NOT init (init might call renderPostElement again internally)
            if (typeof window.UTDfnc.initialize.renderPostElement === 'function') {
              console.log("✅ Calling renderPostElement ONLY (not init)...");
              window.UTDfnc.initialize.renderPostElement();
            }
            
            // Don't call init() - it seems to cause duplicate rendering
            if (typeof window.UTDfnc.initialize.init === 'function') {
              console.log("Calling init...");
              window.UTDfnc.initialize.init();
            }
            
            // Disable automatic rendering again after our call
            if (window.UTDfnc._manualInitOnly) {
              window.UTDfnc._allowRender = false;
            }
            
            this.hasInitialized = true;
            this.isInitializing = false;
            console.log("UTD initialization completed successfully");
            
            // Complete loading bar
            this.completeLoadingBar();
          } catch (error) {
            console.error("Error during UTD initialization:", error);
            this.isInitializing = false;
          }
        } else if (attemptCount >= maxAttempts) {
          clearInterval(interval);
          this.isInitializing = false;
          console.warn("UTDfnc not available after max attempts");
        }
      }, 100);
    },
    loadDynamicScripts() {
      try {
        // Set global variables first
        if (typeof window !== "undefined") {
          window.utdElementSiteUrl = this.siteId;
          window.BLOGGER_URL = "https://selectairintranet.wpcomstaging.com/";
          window.BLOG_PLATFORM = "wordpress";
          window.WORDPRESS_URL = "https://selectairintranet.wpcomstaging.com/";
          
          // Set flag to prevent auto-initialization by the script
          // The script checks this flag before auto-running
          window.UTD_MANUAL_INIT = true;
          window.UTD_DISABLE_AUTO_INIT = true;
          
          console.log("Set UTD flags to prevent auto-initialization");
        }

        // Load external scripts dynamically with better error handling
        const scripts = [
          "https://unpkg.com/swiper@6.1.2/swiper-bundle.min.js",
          "https://cdnjs.cloudflare.com/ajax/libs/jquery/3.4.1/jquery.min.js",
          "https://cdnjs.cloudflare.com/ajax/libs/jquery.isotope/3.0.6/isotope.pkgd.min.js",
          "https://uptodatewebdesign.s3.eu-west-3.amazonaws.com/cdn/dist/v2/app.min.js",
        ];

        // Use setTimeout to avoid immediate execution that might conflict with webpack
        setTimeout(() => {
          this.loadScriptsSequentially(scripts, 0);
        }, 100);
      } catch (error) {
        console.error("Error in loadDynamicScripts:", error);
      }
    },
    loadScriptsSequentially(scripts, index) {
      try {
        if (index >= scripts.length) {
          console.log("All scripts loaded successfully");
          
          // Add delay to ensure script has settled before showing elements and initializing
          setTimeout(() => {
            // Restore any hidden elements before initialization
            if (this.hiddenElements) {
              console.log(`Restoring ${this.hiddenElements.length} hidden elements...`);
              this.hiddenElements.forEach(({ element, display }) => {
                element.style.display = display;
                element.classList.remove('utd-temp-hidden');
              });
              this.hiddenElements = null;
            }
            
            // Add another small delay before initialization
            setTimeout(() => {
              // Now run initialization since all scripts are loaded
              this.runUTDInitialization();
            }, 200);
          }, 500);
          return;
        }

        // Check if script already exists to avoid duplicates
        const existingScript = document.querySelector(
          `script[src="${scripts[index]}"]`
        );
        if (existingScript) {
          console.log(`Script already exists: ${scripts[index]}`);
          this.loadScriptsSequentially(scripts, index + 1);
          return;
        }

        const script = document.createElement("script");
        script.src = scripts[index];
        script.crossOrigin = "anonymous";

        // Don't make the UTD script async as it might need synchronous execution
        if (!scripts[index].includes("app.min.multi-platform.js") && !scripts[index].includes("app.min.js")) {
          script.async = true;
        }

        // Set up event handlers with better error handling
        const handleLoad = () => {
          console.log(`Script loaded: ${scripts[index]}`);

          // Check what globals are available after each script loads
          if (scripts[index].includes("app.min.multi-platform.js")) {
            setTimeout(() => {
              console.log("UTD script loaded, checking globals:", {
                UTDfnc: typeof window.UTDfnc,
                utdElementSiteUrl: window.utdElementSiteUrl,
                windowKeys: Object.keys(window).filter((key) =>
                  key.includes("UTD")
                ),
                scriptContent: script.innerHTML || "external script",
              });

              // Try to manually trigger any initialization if needed
              if (typeof window.UTDfnc === "undefined") {
                console.warn(
                  "UTDfnc not found, script may not have executed properly"
                );
                // You might need to manually trigger script execution here
                this.checkScriptExecution();
              }
            }, 200);
          }

          script.removeEventListener("load", handleLoad);
          script.removeEventListener("error", handleError);
          // Load next script with a small delay
          setTimeout(() => {
            this.loadScriptsSequentially(scripts, index + 1);
          }, 100);
        };

        const handleError = (e) => {
          console.warn(`Failed to load script: ${scripts[index]}`, e);
          script.removeEventListener("load", handleLoad);
          script.removeEventListener("error", handleError);
          // Continue with next script even if one fails
          setTimeout(() => {
            this.loadScriptsSequentially(scripts, index + 1);
          }, 50);
        };

        script.addEventListener("load", handleLoad);
        script.addEventListener("error", handleError);

        // Add script to head
        if (document.head) {
          document.head.appendChild(script);
        }
      } catch (error) {
        console.warn("Error in loadScriptsSequentially:", error);
        // Continue with next script
        setTimeout(() => {
          if (index + 1 < scripts.length) {
            this.loadScriptsSequentially(scripts, index + 1);
          }
        }, 100);
      }
    },
    cleanupScripts() {
      // Remove dynamically loaded scripts
      const scripts = [
        "https://unpkg.com/swiper@6.1.2/swiper-bundle.min.js",
        "https://cdnjs.cloudflare.com/ajax/libs/jquery/3.4.1/jquery.min.js",
        "https://cdnjs.cloudflare.com/ajax/libs/jquery.isotope/3.0.6/isotope.pkgd.min.js",
        "https://uptodatewebdesign.s3.eu-west-3.amazonaws.com/cdn/dist/v2/app.min.multi-platform.js",
      ];

      scripts.forEach((src) => {
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
          existingScript.remove();
        }
      });
    },
    checkScriptExecution() {
      console.log("Checking script execution environment:", {
        location: window.location.href,
        documentReady: document.readyState,
        hasDOM: !!document.getElementById,
        userAgent: navigator.userAgent.includes("Chrome") ? "Chrome" : "Other",
      });

      // Try to fetch and manually execute the script if needed
      // This is a fallback for cases where the script doesn't execute properly
      const utdScript = document.querySelector(
        'script[src*="app.min.multi-platform.js"]'
      );
      if (utdScript && typeof window.UTDfnc === "undefined") {
        console.log("UTD script element found but function not available");

        // Alternative approach: try to reload the script without async
        setTimeout(() => {
          this.reloadUTDScript();
        }, 500);
      }
    },
    async reloadUTDScript() {
      try {
        console.log("Attempting to reload UTD script...");

        // Remove existing script
        const existingScript = document.querySelector(
          'script[src*="app.min.multi-platform.js"]'
        );
        if (existingScript) {
          existingScript.remove();
        }

        // Create new script element with different approach
        const script = document.createElement("script");
        script.src =
          "https://uptodatewebdesign.s3.eu-west-3.amazonaws.com/cdn/dist/v2/app.min.multi-platform.js";
        script.onload = () => {
          setTimeout(() => {
            console.log("UTD script reloaded, checking again:", {
              UTDfnc: typeof window.UTDfnc,
              allUTDKeys: Object.keys(window).filter((key) =>
                key.toLowerCase().includes("utd")
              ),
            });
          }, 300);
        };

        document.head.appendChild(script);
      } catch (error) {
        console.error("Error reloading UTD script:", error);
      }
    },
    logout() {
      // Clear auth data
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_user");
      localStorage.removeItem("uptodate_token");
      localStorage.removeItem("currentClient");
      // Redirect to login
      this.$router.push("/admin/login");
    },
    // Sidebar methods
    handleContentClick(event) {
      console.log("handleContentClick", event);

      // Check if clicked on a team member element
      const teamMember = event.target.closest('.post__item, .team__member');
      const isInTeamSection = event.target.closest('.utd-elements-type-teams, .team');

      if (teamMember && isInTeamSection) {
        console.log('Team member clicked!', teamMember);
        event.preventDefault();
        event.stopPropagation();
        this.openTeamPopup(teamMember);
        return;
      }

      // Check if the clicked element is a link
      const link = event.target.closest("a");
      console.log("link", link);

      if (link && link.href) {
        if (this.showInSidebar) {
          // For all other links, open in sidebar
          event.preventDefault();

          // Handle relative URLs that start with /
          let finalUrl = link.href;
          const originalHref = link.getAttribute("href");

          console.log("Original href attribute:", originalHref);
          console.log("Computed href:", link.href);

          // If the original href starts with / but the computed href doesn't include our base domain
          if (
            originalHref &&
            originalHref.startsWith("/") &&
            !originalHref.startsWith("//")
          ) {
            const baseUrl = "https://selectairintranet.wpcomstaging.com";
            finalUrl = baseUrl + originalHref;
            console.log(
              "Converted relative URL:",
              originalHref,
              "->",
              finalUrl
            );
          }

          const title =
            link.textContent?.trim() ||
            link.title ||
            this.getUrlTitle(finalUrl);
          console.log("Opening in sidebar:", finalUrl, title);
          this.openInSidebar(finalUrl, title);
        } else {
          //Skip if link has target="_blank" or is a download link
          if (link.target === "_blank" || link.download) {
            return; // Let the default behavior happen
          }

          // Skip internal anchors (same page navigation)
          if (
            link.href.includes("#") &&
            link.href.split("#")[0] === window.location.href.split("#")[0]
          ) {
            return; // Let the default behavior happen
          }

          // Skip javascript: links
          if (link.href.startsWith("javascript:")) {
            return; // Let the default behavior happen
          }

          // Skip mailto: and tel: links
          if (link.href.startsWith("mailto:") || link.href.startsWith("tel:")) {
            return; // Let the default behavior happen
          }

          // Skip ALL external links - let them open normally in a new tab
          try {
            const linkUrl = new URL(link.href);
            const currentHost = window.location.hostname;

            // List of internal domains that should NOT open in new tab
            const internalDomains = [
              'intranet.selectair.be',
              'selectairintranet.wpcomstaging.com',
              'localhost'
            ];

            const isInternalDomain = internalDomains.some(domain =>
              linkUrl.hostname === domain || linkUrl.hostname.includes(domain)
            ) || linkUrl.hostname === currentHost;

            // If link is to a different/external domain, open it in a new tab
            if (!isInternalDomain) {
              console.log('External link detected, opening in new tab:', link.href);
              event.preventDefault();
              window.open(link.href, '_blank', 'noopener,noreferrer');
              return;
            }
          } catch (e) {
            // Invalid URL, let it pass through
          }
        }
      }
    },
    openInSidebar(url, title = "") {
      console.log("Opening in sidebar:", url);

      this.sidebarUrl = url;
      this.sidebarTitle = title || this.getUrlTitle(url);
      this.sidebarOpen = true;
      this.sidebarError = null;
      this.sidebarContent = null;

      // Load content
      this.loadSidebarContent();
    },
    closeSidebar() {
      this.sidebarOpen = false;
      this.sidebarContent = "";
      this.sidebarError = null;
      this.sidebarLoading = false;
    },
    injectNewTabLinks(iframe) {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        if (!doc || !doc.querySelectorAll) return;

        const makeNewTab = (link) => {
          const href = link.getAttribute("href");
          if (!href || href.startsWith("#") || href.startsWith("javascript:") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
          link.setAttribute("target", "_blank");
          link.setAttribute("rel", "noopener noreferrer");
        };

        doc.querySelectorAll("a[href]").forEach(makeNewTab);

        // Re-apply for dynamically added links (e.g. JS-rendered content)
        new MutationObserver(() => {
          doc.querySelectorAll("a[href]:not([target='_blank'])").forEach(makeNewTab);
        }).observe(doc.body, { childList: true, subtree: true });
      } catch (e) {
        // Cross-origin iframe — cannot inject, user can use "Open in New Tab" button
      }
    },
    async loadSidebarContent() {
      this.sidebarLoading = true;
      this.sidebarError = null;

      try {
        if (this.isValidUrl(this.sidebarUrl)) {
          this.sidebarContent = "iframe";

          this.$nextTick(() => {
            const iframe = document.querySelector(".sidebar-iframe");
            if (!iframe) return;

            iframe.onload = () => {
              this.sidebarLoading = false;
              // iframe is same-origin (selectairintranet.wpcomstaging.com) so we can
              // access its DOM and patch all links to open in a new tab
              this.injectNewTabLinks(iframe);
            };

            iframe.onerror = () => {
              this.sidebarError = "Content blocked by browser security settings";
            };
          });
        }
      } catch (error) {
        console.error("Error loading sidebar content:", error);
        this.sidebarError = error.message || "Failed to load content";
      } finally {
        this.sidebarLoading = false;
      }
    },
    isValidUrl(url) {
      try {
        const urlObj = new URL(url);
        return ["http:", "https:"].includes(urlObj.protocol);
      } catch {
        return false;
      }
    },
    getUrlTitle(url) {
      try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname.replace("www.", "");
        const pathname = urlObj.pathname;

        if (pathname && pathname !== "/") {
          const lastSegment = pathname.split("/").pop();
          if (lastSegment) {
            return lastSegment.replace(/[-_]/g, " ").replace(/\.[^/.]+$/, "");
          }
        }

        return hostname;
      } catch {
        return url;
      }
    },
    async copySidebarUrl() {
      try {
        await navigator.clipboard.writeText(this.sidebarUrl);
        console.log("URL copied to clipboard");
        // You could add a toast notification here
      } catch (error) {
        console.error("Failed to copy URL:", error);
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = this.sidebarUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
    },
    handleKeydown(event) {
      // Close sidebar on ESC key
      if (event.key === "Escape" && this.sidebarOpen) {
        this.closeSidebar();
      }
      // Close team popup on ESC key
      if (event.key === "Escape" && this.showTeamPopup) {
        this.closeTeamPopup();
      }
    },
    // Team popup methods
    setupTeamClickHandlers() {
      console.log('Setting up team click handlers...');

      // Wait for DOM to be ready with team elements
      setTimeout(() => {
        const teamElements = document.querySelectorAll('.utd-elements-type-teams.team .team__member, .utd-elements-type-teams .post__item');
        console.log(`Found ${teamElements.length} team elements`);

        teamElements.forEach((element) => {
          // Add pointer cursor and click handler
          element.style.cursor = 'pointer';
          element.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.openTeamPopup(element);
          });
        });
      }, 1500);
    },
    openTeamPopup(element) {
      console.log('Opening team popup for element:', element);

      // Try to get data from the DOM element - try multiple selectors
      const nameEl = element.querySelector('.team__name, .post__title, h3, h4');
      const positionEl = element.querySelector('.team__position, .post__subtitle, .post__meta-item');
      const imgEl = element.querySelector('.team__img, .post__img, img');
      const descEl = element.querySelector('.team__desc, .post__desc, .post__excerpt');

      const name = nameEl?.innerText || '';
      const position = positionEl?.innerText || '';
      const img = imgEl?.src || '';
      const description = descEl?.innerText || '';

      // Try to find matching data from window.utils.dynamics.teams (with email/contact)
      let email = '';
      let contact = '';
      let fullDescription = description;
      let fullImage = img;

      // Get team data from window.utils.dynamics.teams
      const teamsData = window.utils?.dynamics?.teams || this.teamMembersData || [];
      console.log('Teams data from window.utils.dynamics.teams:', teamsData);

      if (teamsData && teamsData.length > 0) {
        // Find matching member by name
        const matchingMember = teamsData.find(m =>
          m.name && m.name.toLowerCase().trim() === name.toLowerCase().trim()
        );

        if (matchingMember) {
          console.log('Found matching member with full data:', matchingMember);
          email = matchingMember.email || '';
          contact = matchingMember.contact || '';
          // Also get description and image from API data if available
          if (matchingMember.description) {
            fullDescription = matchingMember.description;
          }
          if (matchingMember.image) {
            fullImage = matchingMember.image;
          }
        }
      }

      this.teamPopupData = {
        name,
        position,
        description: fullDescription,
        image: fullImage,
        email,
        contact
      };

      console.log('Team popup data:', this.teamPopupData);
      this.showTeamPopup = true;
    },
    closeTeamPopup() {
      this.showTeamPopup = false;
    },
    handleTeamsLoaded(event) {
      console.log('🎉 teams-loaded event received in DynamicPage!');
      console.log('Event detail:', event.detail);

      if (event.detail && Array.isArray(event.detail)) {
        this.teamMembersData = event.detail;
        console.log('Team members data stored:', this.teamMembersData);
      }

      // Setup click handlers after team data is loaded
      this.setupTeamClickHandlers();
    },
  },
};
</script>

<style scoped>
/* Top Loading Bar */
.top-loading-bar {
  position: fixed;
  top: 0;
  left: 0;
  height: 3px;
  background: linear-gradient(90deg, #e31c18 0%, #f87171 100%);
  transition: width 0.3s ease;
  z-index: 9999;
  box-shadow: 0 0 10px rgba(227, 28, 24, 0.5);
}

.dynamic-page-container {
  min-height: 400px;
  position: relative;
  z-index: 1;
}

/* Global styles for dynamic content */
.dynamic-content {
  position: relative;
  z-index: 1;
}

.dynamic-content :deep(img) {
  max-width: 100%;
  /* height: auto; */
}

.dynamic-content :deep(a) {
  color: #dc2626;
  /* text-decoration: underline; */
}

.dynamic-content :deep(a:hover) {
  color: #b91c1c;
}

/* Preserve any existing styles from the fetched content */
.dynamic-content :deep(*) {
  word-wrap: break-word;
}

/* Ensure content doesn't override the admin layout */
.dynamic-content :deep(nav) {
  position: static !important;
}

.dynamic-content :deep(.fixed) {
  position: static !important;
}

.dynamic-content :deep(.absolute) {
  position: relative !important;
}

/* Override any global body styles from the fetched content */
.dynamic-content :deep(body) {
  margin: 0 !important;
  padding: 0 !important;
  position: static !important;
}

/* Sidebar specific styles */
.fixed {
  position: fixed !important;
}

/* Smooth transitions for sidebar */
.transform {
  transition: transform 0.3s ease-in-out;
}

/* Ensure sidebar is above everything */
.z-50 {
  z-index: 50;
}

/* Sidebar content scrolling */
.prose {
  max-width: none;
}

.prose :deep(h1),
.prose :deep(h2),
.prose :deep(h3),
.prose :deep(h4),
.prose :deep(h5),
.prose :deep(h6) {
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

.prose :deep(p) {
  margin-bottom: 1rem;
}

.prose :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 0.5rem;
}

.prose :deep(a) {
  color: #dc2626;
  text-decoration: underline;
}

.prose :deep(a:hover) {
  color: #b91c1c;
}

/* Enhanced iframe styling */
.prose :deep(iframe) {
  border: none;
  border-radius: 0.5rem;
}

/* Loading spinner for sidebar */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Mobile responsive adjustments for sidebar */
@media (max-width: 640px) {
  .sidebar-content {
    padding: 1rem;
  }

  .sidebar-header h2 {
    font-size: 1rem;
  }

  .sidebar-footer {
    flex-direction: column;
    gap: 0.5rem;
  }

  .sidebar-footer .flex {
    flex-direction: column;
    width: 100%;
  }

  .sidebar-footer button,
  .sidebar-footer a {
    width: 100%;
    justify-content: center;
  }
}

/* Team Popup Styles */
.team-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.team-popup-overlay .team-popup-container {
  width: 100%;
  max-width: 80rem;
  margin: 0 auto;
}

.team-popup-overlay .team-popup-content {
  background-color: #fff;
  position: relative;
  padding: 3rem;
  border-radius: 1rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.team-popup-overlay .team-popup-close {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  width: 4rem;
  height: 4rem;
  background-color: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 2rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: background-color 0.2s;
}

.team-popup-overlay .team-popup-close:hover {
  background-color: #e5e7eb;
}

.team-popup-overlay .member-lightbox {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
}

@media (min-width: 768px) {
  .team-popup-overlay .member-lightbox {
    grid-template-columns: 30% 70%;
  }
}

.team-popup-overlay .profile-lt-box {
  height: 25rem;
  overflow: hidden;
  border-radius: 0.8rem;
}

@media (min-width: 768px) {
  .team-popup-overlay .profile-lt-box {
    height: 30rem;
  }
}

.team-popup-overlay .popup-team-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
}

.team-popup-overlay .text-bold {
  font-weight: 600;
}

.team-popup-overlay .mb-3 {
  margin-bottom: 1rem;
}

.team-popup-overlay .mb-4 {
  margin-bottom: 1.5rem;
}

.team-popup-overlay .mb-2 {
  margin-bottom: 0.8rem;
}

.team-popup-overlay .mb-0 {
  margin-bottom: 0;
}

.team-popup-overlay h3 {
  font-size: 2.4rem;
  color: #111827;
  margin: 0;
  font-weight: 500;
}

.team-popup-overlay p {
  margin: 0;
  color: #4b5563;
  font-size: 1.6rem;
}

.team-popup-overlay .popup_desc {
  white-space: pre-wrap;
  color: #6b7280;
  font-size: 1.5rem;
  line-height: 1.6;
}

.team-popup-overlay .team-contact-info {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
}

.team-popup-overlay .team-contact-info p {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.6rem;
}

.team-popup-overlay .contact-icon {
  font-size: 1.8rem;
}

.team-popup-overlay .text-theme {
  color: var(--theme, #e31c18);
}

.team-popup-overlay .text-theme:hover {
  text-decoration: underline;
}

.team-popup-overlay .flex {
  display: flex;
}

.team-popup-overlay .flex-column {
  flex-direction: column;
}

.team-popup-overlay .f-justify-center {
  justify-content: center;
}

.team-popup-overlay .items-center {
  align-items: center;
}
</style>
